import React, { useState, useEffect, useRef } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import Select from "react-select";
import { get, post } from "../../services/api";
import { toast } from "react-toastify";
import logo from "../../assets/img/logo-white.png";
import studentPhoto from "../../assets/img/generic/student-girl.jpg";
// import "./RegistrationForm.css";
import { NavLink } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import TagModal from "../../components/TagModal";
import { createPortal } from "react-dom";
// import { useFormContext } from "./RegistrationFormContext";

const StudentDocuments = () => {
  const token = localStorage.getItem("token");
  const userId = localStorage.getItem("userId");
  const roleId = localStorage.getItem("roleId");

  const {
    register,
    watch,
    control,
    handleSubmit,
    setValue,
    getValues,
    reset,
    trigger,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {},
  });

  const [pupils, setPupils] = useState([]);
  const [selectedPupilId, setSelectedPupilId] = useState(null);
  const [pupilError, setPupilError] = useState(false);

  const [tagModalData, setTagModalData] = useState({
    isOpen: false,
    field: null,
    index: null,
    tagName: "",
    tagComments: "",
    tagPriority: null,
  });

  useEffect(() => {
    const fetchPupils = async () => {
      try {
        const response = await get(
          `Student/GetPupilsInformationByUserId?userId=${userId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setPupils(response);

        if (response.length > 0) {
          const firstPupilId = response[0].id;
          setSelectedPupilId(firstPupilId);

          handlePupilSelection({ target: { value: firstPupilId } });
        }
      } catch (error) {
        console.error("Error fetching pupils:", error);
      }
    };

    fetchPupils();
  }, []);

  const [uploadedFiles, setUploadedFiles] = useState({
    pupilPhoto: [],
    birthCertificate: [],
    residencyIdNonSaudis: [],
    fatherResidencyIdNonSaudis: [],
    familyIdCardSaudis: [],
    fatherIdCardSaudis: [],
    studentPassport: [],
    fatherPassport: [],
    motherPassport: [],
    vaccinationCertificate: [],
    previousSchoolReports: [],
    equivalenceCertificate: [],
    schoolLeavingCertificate: [],
  });

  const fileInputRefs = useRef({});

  const handleTagClick = (field, index) => {
    const file = uploadedFiles[field][index];
    setTagModalData({
      isOpen: true,
      field,
      index,
      tagName: file.tagName || "",
      tagComments: file.tagComments || "",
      tagPriority: file.tagPriority || null,
      documentId: file.id || 0,
    });
  };

  const handleTagSave = (data) => {
    setUploadedFiles((prev) => ({
      ...prev,
      [tagModalData.field]: prev[tagModalData.field].map((file, i) =>
        i === tagModalData.index
          ? {
              ...file,
              tagName: data.tagName,
              tagComments: data.tagComments,
              tagPriority: data.tagPriority,
            }
          : file
      ),
    }));

    setTagModalData({
      isOpen: false,
      field: null,
      index: null,
      tagName: "",
      tagComments: "",
      tagPriority: null,
      documentId: 0,
    });
  };

  const handleTagClose = () => {
    setTagModalData({
      isOpen: false,
      field: null,
      index: null,
      tagName: "",
      tagComments: "",
      tagPriority: null,
      documentId: 0,
    });
  };

  const handleFileChange = (field) => async (event) => {
    const files = Array.from(event.target.files);

    if (files.length === 0) return; // Prevents unnecessary updates

    if (field === "pupilPhoto") {
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadedFiles((prev) => ({
          ...prev,
          pupilPhoto: [
            {
              id: 0,
              createdBy: localStorage.getItem("userId"),
              updatedBy: localStorage.getItem("userId"),
              fileName: files[0].name,
              filePath: "",
              base64String: reader.result.split(",")[1], // Extract Base64
            },
          ], // Store in an array but limit to one file
        }));
      };
      reader.readAsDataURL(files[0]);
    } else {
      const filePromises = files.map((file) => {
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            resolve({
              id: 0,
              createdBy: localStorage.getItem("userId"),
              updatedBy: localStorage.getItem("userId"),
              fileName: file.name,
              filePath: "",
              base64String: reader.result.split(",")[1], // Extract Base64
            });
          };
          reader.readAsDataURL(file);
        });
      });

      const fileDataArray = await Promise.all(filePromises);

      setUploadedFiles((prev) => ({
        ...prev,
        [field]: [...prev[field], ...fileDataArray], // Append all new files at once
      }));
    }

    // Reset the input field after files are selected
    if (fileInputRefs.current[field]) {
      fileInputRefs.current[field].value = "";
    }
  };

  const handlePreviewFile = (file) => {
    const isPDF = file.fileName.toLowerCase().endsWith(".pdf");

    if (file.base64String) {
      const byteCharacters = atob(file.base64String);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], {
        type: isPDF ? "application/pdf" : "image/jpeg",
      });

      const fileUrl = URL.createObjectURL(blob);
      window.open(fileUrl, "_blank");

      setTimeout(() => URL.revokeObjectURL(fileUrl), 5000);
    } else if (file.filePath) {
      const attachmentUrl =
        process.env.REACT_APP_LIVE_DOMAIN_URL +
        "/ExternalImages/" +
        file.filePath;
      window.open(attachmentUrl, "_blank");
    }
  };

  const handleRemoveFile = (field, index = null) => {
    if (index === null) {
      setUploadedFiles((prev) => ({ ...prev, [field]: [] }));
    } else {
      const newFiles = uploadedFiles[field].filter((_, i) => i !== index);
      setUploadedFiles((prev) => ({ ...prev, [field]: newFiles }));
    }
  };

  const onSaveForm = async () => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    if (!selectedPupilId) {
      setPupilError(true);
      return;
    } else {
      setPupilError(false);
    }

    const pupilPhotoDocument =
      uploadedFiles.pupilPhoto.length > 0
        ? [
            {
              id: uploadedFiles.pupilPhoto[0].id || 0,
              createdBy: localStorage.getItem("userId"),
              updatedBy: localStorage.getItem("userId"),
              fileName: uploadedFiles.pupilPhoto[0].fileName,
              documentType: "pupilPhoto",
              base64String: uploadedFiles.pupilPhoto[0].base64String || "",
              filePath: uploadedFiles.pupilPhoto[0].filePath || "",
            },
          ]
        : [];

    const documentFields = Object.keys(uploadedFiles).filter(
      (field) => field !== "pupilPhoto"
    );

    const studentDocuments = [
      ...pupilPhotoDocument,
      ...documentFields.flatMap((field) =>
        uploadedFiles[field].map((file) => ({
          id: file.id || 0,
          createdBy: localStorage.getItem("userId"),
          updatedBy: localStorage.getItem("userId"),
          fileName: file.fileName,
          documentType: field,
          base64String: file.base64String || "",
          filePath: file.filePath || "",
        }))
      ),
    ];

    const formData = {
      studentFirstRegistrationId: selectedPupilId,
      studentDocuments,
    };

    try {
      const response = await post(
        "Student/UpdateAdmissionInqueryAttachments",
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.isSuccess) {
        toast.success("Success!");
      } else {
        toast.error(response.message || "An unexpected error occurred");
      }
    } catch (error) {
      console.error("Error.", error);
    }
  };

  const handlePupilSelection = async (event) => {
    const pupilId = event.target.value;
    setSelectedPupilId(pupilId);
    setPupilError(false);

    if (!pupilId) {
      setPupilError(true);
      return;
    }

    try {
      const response = await get(
        `Student/GetAdmissionInqueryAttachments?studentFirstRegistrationId=${pupilId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response) {
        // Reset all files first
        const updatedFiles = Object.keys(uploadedFiles).reduce((acc, key) => {
          acc[key] = [];
          return acc;
        }, {});

        // Process the API response
        response.studentDocuments.forEach((document) => {
          const docType = document.documentType; // Ensure this matches uploadedFiles keys
          if (updatedFiles.hasOwnProperty(docType)) {
            updatedFiles[docType].push({
              id: document.id,
              fileName: document.fileName,
              filePath: document.filePath,
              base64String: document.base64String || "",
              createdBy: document.createdBy,
              updatedBy: document.updatedBy,
              tagName: document.tagName || "",
              tagComments: document.tagComments || "",
              tagPriority: document.tagPriority || null,
            });
          }
        });

        setUploadedFiles(updatedFiles);
      }
    } catch (error) {
      console.error("Error fetching attachments:", error);
    }
  };

  const getFileExtension = (fileName) => {
    return fileName?.split(".").pop();
  };

  const formatFileName = (field, fileName, count) => {
    const documentTypeName = field
      .replace(/([A-Z])/g, " $1")
      .toLowerCase()
      .trim()
      .replace(/\b\w/g, (char) => char.toUpperCase());

    const fileExtension = getFileExtension(fileName); // Get the file extension

    if (field === "pupilPhoto") {
      return `${documentTypeName}.${fileExtension}`; // Simply return the file name for pupil photo
    }

    return `${documentTypeName}-${count}.${fileExtension}`; // Format the name for other files
  };

  return (
    <>
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading fs-7">Student Documents</h2>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-12">
              <div className="mb-3" style={{ width: "200px" }}>
                <label className="form-label">
                  Select Pupil <span className="text-danger fs-9">*</span>
                </label>
                <select
                  className={`form-select`}
                  onChange={handlePupilSelection}
                  value={selectedPupilId}
                >
                  <option value="">Select</option>
                  {pupils.map((pupil) => (
                    <option key={pupil.id} value={pupil.id}>
                      {pupil.firstName + " " + pupil.lastName}
                    </option>
                  ))}
                </select>
                {pupilError && (
                  <small className="text-danger">This field is required.</small>
                )}
              </div>

              {selectedPupilId && (
                <>
                  <div className="row g-3 gy-4">
                    {Object.keys(uploadedFiles).map((field) => (
                      <div className="col-md-3" key={field}>
                        <label className="form-label">
                          {field.replace(/([A-Z])/g, " $1")}
                        </label>
                        <div className="attachment-wrapper">
                          <div className="attachment-preview mt-2">
                            {uploadedFiles[field].length > 0
                              ? uploadedFiles[field].map((file, index) => {
                                  const isFromAPI = file.id !== 0;
                                  const isParentRole = [
                                    "9",
                                    "10",
                                    "11",
                                  ].includes(roleId);

                                  return (
                                    <div
                                      key={index}
                                      className="file-item mb-1 d-flex justify-content-between align-items-center position-relative"
                                    >
                                      <div className="att-file-name">
                                        {isFromAPI
                                          ? file.fileName
                                          : formatFileName(
                                              field,
                                              file.fileName,
                                              index + 1
                                            )}
                                      </div>

                                      <div className="d-flex gap-1">
                                        <span
                                          className="attachment-action-btn preview-btn"
                                          onClick={() =>
                                            handlePreviewFile(file)
                                          }
                                          data-tooltip-id="global-tooltip"
                                          data-tooltip-content="Preview"
                                        >
                                          <i className="fa fa-eye"></i>
                                        </span>
                                        {isFromAPI &&
                                          (!isParentRole || file.tagName) && (
                                            <span
                                              className="attachment-action-btn tag-btn"
                                              onClick={() =>
                                                handleTagClick(field, index)
                                              }
                                              data-tooltip-id="global-tooltip"
                                              data-tooltip-content="Tag"
                                            >
                                              {file.tagName ? (
                                                <span className="badge bg-primary">
                                                  {file.tagName}
                                                </span>
                                              ) : (
                                                <i className="fa fa-tag"></i>
                                              )}
                                            </span>
                                          )}
                                        {/* <span
                                      className="attachment-action-btn remove-btn"
                                      onClick={() =>
                                        handleRemoveFile(field, index)
                                      }
                                      data-tooltip-id="global-tooltip"
                                      data-tooltip-content="Remove"
                                    >
                                      <i className="fa fa-remove"></i>
                                    </span> */}
                                      </div>
                                    </div>
                                  );
                                })
                              : null}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* <div className="mt-4">
              <button
                type="button"
                className="btn btn-sm btn-dark"
                onClick={onSaveForm}
              >
                Save Attachments
              </button>
            </div> */}
          </div>
        </div>

        {tagModalData.isOpen &&
          createPortal(
            <TagModal
              tagName={tagModalData.tagName}
              tagComments={tagModalData.tagComments}
              tagPriority={tagModalData.tagPriority}
              onSave={handleTagSave}
              onClose={handleTagClose}
              documentId={tagModalData.documentId}
              studentFirstRegistrationId={selectedPupilId}
            />,
            document.body
          )}
      </div>
    </>
  );
};

export default StudentDocuments;
